/* Additive below-fold reveals: each marked block rises and fades in as its first edge
   enters the screen, and blocks in a row can arrive one after another
   (data-hsf-v10-motion-delay, milliseconds). No initial hidden state in CSS, no timers,
   scroll listeners, image fetching, autoplay, or purchase-control animation.
   2026-10-13: three kinds of arrival. Text rises a short way; a picture settles into its
   frame; a section heading is uncovered from its baseline. Blocks that arrive together in
   one row follow each other 70 ms apart unless they carry their own delay. */
(() => {
  'use strict';
  if (window.HsfV10Motion) return;
  window.HsfV10Motion = true;
  if (!('IntersectionObserver' in window) || !window.matchMedia) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const selector = '[data-hsf-v10-motion], .hsf-pdp--v10 .hsf-pdp__faq, .hsf-pdp-badges > li, .hsf-pdp-intro--more > *, .hsf-link-network__collections, .hsf-v10-info-section > *';
  const EASE = 'cubic-bezier(.22,1,.36,1)';
  const kinds = {
    rise: { frames: [{ opacity: 0, transform: 'translateY(16px)' }, { opacity: 1, transform: 'none' }], duration: 560 },
    picture: { frames: [{ opacity: 0, transform: 'translateY(18px) scale(.985)' }, { opacity: 1, transform: 'none' }], duration: 760 },
    heading: { frames: [{ opacity: 0, transform: 'translateY(10px)', clipPath: 'inset(0 0 100% 0)' }, { opacity: 1, transform: 'none', clipPath: 'inset(-12% -4% -22% -4%)' }], duration: 640 },
    badge: { frames: [{ opacity: 0, transform: 'translateY(8px) scale(.94)' }, { opacity: 1, transform: 'none' }], duration: 420 }
  };
  const kindOf = element => {
    if (element.matches('.hsf-pdp-badges > li')) return 'badge';
    if (element.matches('figure, picture, [class*="__media"], [class*="__photo"], [class*="__image"], [class*="__portrait"]')) return 'picture';
    if (element.matches('h2, h3, header, [class*="section-heading"], [class*="__heading"]') && element.textContent.trim().length < 220) return 'heading';
    return 'rise';
  };
  const purchase = 'form, product-form-component, [data-hsf-v10-purchase], .hsf-mobile-buybar, .hsf-curated-native-gallery, .cart-drawer';
  const seen = new WeakSet();
  const active = new Map();
  let observer;

  const cancel = element => {
    const animation = active.get(element);
    if (animation) { animation.cancel(); active.delete(element); }
  };
  function start(scope = document) {
    if (reduced.matches) return;
    if (!observer) observer = new IntersectionObserver(entries => {
      const row = new Map();
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) return;
        observer.unobserve(target);
        if (reduced.matches || !target.isConnected || target.matches(':focus-within')) return;
        try {
          // The full-opacity CSS base survives blocked scripts or API failure.
          // Starts the moment the first pixel enters, so nothing visible ever blinks out.
          const place = row.get(target.parentElement) || 0;
          row.set(target.parentElement, place + 1);
          const own = target.dataset.hsfV10MotionDelay;
          const delay = Math.min(own !== undefined && own !== '' ? Number(own) || 0 : place * 70, 420);
          const kind = kinds[kindOf(target)];
          const animation = target.animate(kind.frames, { duration: kind.duration, delay, easing: EASE, fill: 'backwards' });
          active.set(target, animation);
          animation.finished.then(() => active.delete(target), () => active.delete(target));
        } catch (_) { /* Content already visible; enhancement is optional. */ }
      });
    }, { threshold: 0 });
    scope.querySelectorAll(selector).forEach(element => {
      if (seen.has(element) || element.closest(purchase) || element.querySelector(purchase)) return;
      seen.add(element);
      // Above-fold content and anchors already passed never fade after paint.
      if (element.getBoundingClientRect().top < window.innerHeight) return;
      if (typeof element.animate === 'function') observer.observe(element);
    });
  }
  const onPreferenceChange = () => {
    if (!reduced.matches) { start(); return; }
    observer?.disconnect();
    active.forEach((_, element) => cancel(element));
  };
  if (reduced.addEventListener) reduced.addEventListener('change', onPreferenceChange);
  else reduced.addListener?.(onPreferenceChange);
  document.addEventListener('focusin', event => {
    const element = event.target.closest?.(selector);
    if (element) { observer?.unobserve(element); cancel(element); }
  });
  document.addEventListener('shopify:section:load', event => start(event.target));
  document.addEventListener('shopify:section:unload', () => {
    active.forEach((_, element) => { if (!element.isConnected) cancel(element); });
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => start(), { once: true });
  else start();
})();
